/**
 * Clients, as a report.
 *
 * The page used to be a table of the book with tiles over it, which
 * answers "find me this account" and nothing else. What gets asked of
 * 1,332 clients is four other things, and each of them has a block here:
 *
 *   the plans      how many on FREE, STARTER, GROWTH, SUITE — and what
 *                  each tier is worth
 *   the codes      who arrived on a promo code, and which codes those were
 *   the money      who has actually paid, on what cycle
 *   the usage      who is using the product, over four windows
 *
 * It is what the Clients menu lands on. "Find me this account" is a real
 * question and it has a whole page of its own next door on Clients; this
 * one is for the questions a book of 1,332 gets asked that a table cannot
 * answer.
 *
 * ## Every figure opens on its rows
 *
 * A number nobody can open is a number nobody can act on, and the first
 * thing anybody asks of a surprising one is "which ones?". So each tile,
 * each plan card, each code and each half of each activity window is a
 * button onto the accounts it was counted from — see ClientDetail.
 *
 * ## The window, and the two figures it does not move
 *
 * The date range picks accounts by **when they signed up**, the same
 * measure every other filter over this book uses. The activity windows
 * count back from today regardless, because "who has been in this week"
 * does not change because somebody narrowed the page to March.
 */
import { useMemo, useState } from 'react';
import { useApp } from '../store/AppContext.jsx';
import api from '../lib/api.js';
import { download } from '../lib/format.js';
import { parseDate, addDays } from '../lib/dates.js';
import Icon from '../lib/icons.jsx';
import { Card, CardBody, EmptyState, Spinner } from '../components/Primitives.jsx';
import ClientDetail from '../components/modals/ClientDetail.jsx';
import { SEEN_WINDOWS } from '../domain/derive.js';
import { TZ_LABEL, stamp } from '../domain/demos.js';
import {
  RANGES, rangeOf, dayOf, summarise, plansOf, ownersOf, share, lineOf,
  windowRow
} from '../domain/clientReport.js';

/*
 * The six figures across the top, in the order the sentence needs them.
 *
 * `cut` is what each one opens, so the tile and the sheet behind it are
 * always the same rows. `of` names what the percentage is a share of —
 * left off the first, which is the whole.
 */
/*
 * The six figures that head both exports.
 *
 * They are no longer drawn on screen — the two activity cards took the
 * top of the page — but a report that opens with "here is the window" and
 * a file that opens with a summary are different documents, and the file
 * is the one that gets forwarded to somebody who cannot click anything.
 *
 * Built per render rather than declared once, because the activity figure
 * has to name the window the period named: picking Today and being told
 * about "this week" is the file answering its own question.
 */
const headlineFor = days => [
  { k: 'total', l: 'Clients', i: 'users', c: 'g-purple', s: 'on the book in this window', cut: 'all' },
  { k: 'paid', l: 'Payment done', i: 'card', c: 'g-green', s: 'the payment column says they have paid', cut: 'paid' },
  /* The amounts on the paying accounts, added up - the same figure the
     Paid card and the plan cards now carry. It used to be a monthly
     rate, which is why one tile said 807 while the card under it said
     4,527 of the same money. */
  { k: 'value', l: 'Collected', i: 'chart', c: 'g-cyan', s: 'from the accounts that have paid', cut: 'paid', money: true },
  { k: 'promo', l: 'On a promo code', i: 'tag', c: 'g-amber', s: 'arrived on a code', cut: 'promo' },
  {
    k: 'seen',
    /* windowRow, so every window reads the same way and none of them
       says "this week" when the filter says something else. */
    l: `Active · ${windowRow(days)}`,
    i: 'zap',
    c: 'g-blue',
    s: `have opened XYRA ${days === 1 ? 'today' : `in the last ${days} days`}`,
    cut: `seen:${days}`
  },
  { k: 'never', l: 'Never signed in', i: 'eyeoff', c: 'g-slate', s: 'the app has never seen them', cut: 'never' }
];

/* A colour per plan card, cycled by rank. Decoration: which plan a card is
   about is said by its name, and the money tones inside carry the meaning. */
const ACCENTS = ['a-indigo', 'a-teal', 'a-violet', 'a-amber', 'a-sky', 'a-rose', 'a-lime', 'a-coral'];

/** An icon for the tiers, so a card is recognisable before its name is read. */
const PLAN_ICONS = {
  FREE: 'flag', STARTER: 'zap', GROWTH: 'chart', SUITE: 'star',
  PROFESSIONAL: 'card', TRIAL: 'clock', 'NO PLAN': 'eyeoff'
};

/**
 * How much of a plan's accounts pay, as a ring.
 *
 * Drawn from a circle's dash rather than an arc path, so there is no
 * geometry to get wrong at 0% or 100%.
 */
function Ring({ pct }) {
  const r = 17;
  const c = 2 * Math.PI * r;
  return (
    <span className="crring" aria-hidden="true">
      <svg viewBox="0 0 40 40">
        <circle className="crrbg" cx="20" cy="20" r={r} />
        <circle className="crrfg" cx="20" cy="20" r={r} strokeDasharray={`${(c * (pct || 0)) / 100} ${c}`} />
      </svg>
      <b>{pct}%</b>
    </span>
  );
}

/**
 * One plan, as a card.
 *
 * The count leads, a bar under it says how much of that tier pays without
 * anybody reading a number, four boxes carry the details asked of a tier,
 * and the figure the card exists for — what the tier is worth a month —
 * gets a panel of its own at the foot.
 */
function PlanCard({ p, of, rank, money, days, period, onOpen }) {
  const open = cut => onOpen(`plan:${p.key}${cut ? `:${cut}` : ''}`);
  const paidPct = share(p.paid, p.total) || 0;

  const boxes = [
    ['paid', 'Payment done', p.paid, 'card'],
    ['unpaid', 'Not paid', p.total - p.paid, 'eyeoff'],
    /* windowRow, not windowLabel: the latter answers "in 14 days",
       so the box read "In in 14 days" for every window but today and
       this week. It also has to name the window rather than hide it —
       the box moves with the filter and "In this week" said so only
       by accident, when the filter happened to be seven days. */
    ['seen', windowRow(days), p.seen, 'zap'],
    ['promo', 'On a code', p.promo, 'tag']
  ];

  return (
    <li className={`crcard ${ACCENTS[(rank - 1) % ACCENTS.length]}`}>
      <header>
        <span className="crcic"><Icon name={PLAN_ICONS[p.key] || 'card'} solid /></span>
        <button type="button" className="crcname" onClick={() => open('')}>{p.key}</button>
        <span className="crcrank">{p.price ? money(p.price) : 'free'}</span>
      </header>

      <button type="button" className="crchero" onClick={() => open('')}>
        <b>{p.total.toLocaleString()}</b>
        <span>client{p.total === 1 ? '' : 's'}</span>
        <em>{share(p.total, of)}% of the book</em>
      </button>

      {/* Paying against the rest of the tier. Every card's bar is full
      width, so cards are compared on their mix, not their size. */}
      <div className="crbar">
        <button
      type="button"
      className="crseg paid"
      style={{ flexGrow: Math.max(p.paid, 0) }}
      onClick={() => open('paid')}
      title={`${p.paid} paying — ${paidPct}%`}
      aria-label={`${p.paid} paying`}
        />
        <button
      type="button"
      className="crseg unpaid"
      style={{ flexGrow: Math.max(p.total - p.paid, 0) }}
      onClick={() => open('unpaid')}
      title={`${p.total - p.paid} with nothing recorded`}
      aria-label={`${p.total - p.paid} not paid`}
        />
      </div>

      <ul className="crgrid">
        {boxes.map(([k, label, n, icon]) => (
      <li key={k}>
        <button
          type="button"
          className={`crbox ${k}${n ? '' : ' nil'}`}
          onClick={() => (n ? open(k) : null)}
          disabled={!n}
        >
          <span className="crblab"><Icon name={icon} />{label}</span>
          <span className="crbnum">
            <b>{n ? n.toLocaleString() : '—'}</b>
            {n ? <em>{share(n, p.total)}%</em> : null}
          </span>
        </button>
      </li>
        ))}
      </ul>

      {/*
        * What the paying accounts on this plan add up to.
        *
        * The amounts as they stand, added together — not a monthly rate.
        * A rate divided an annual invoice by twelve, so a card reading
        * AED 421 opened on a list adding to AED 1,430 and there was
        * nothing on either to say why. The figure and the list it opens
        * are now the same arithmetic.
        *
        * The period is named on the label, because the number moves when
        * the filter above does and a total with no window is a number
        * nobody can check.
        */}
      <button
        type="button"
        className={`crworth${p.value ? '' : ' nil'}`}
        onClick={() => (p.paid ? open('paid') : null)}
        disabled={!p.paid}
        title={`${p.paid} paying account${p.paid === 1 ? '' : 's'} on this plan, added up`}
      >
        <span>
      <small>Collected · {period}</small>
      <b>{p.value ? money(p.value) : '—'}</b>
        </span>
        <Ring pct={paidPct} />
      </button>
    </li>
  );
}

/**
 * Every promo code, and how many accounts came in on it.
 *
 * The first version showed the top forty with six client names printed
 * under each — which is a wall of names nobody reads, hides the two
 * hundred codes below the fold, and answers a question ("who exactly")
 * that the row already opens on when it is clicked.
 *
 * So: one line per code, all of them, with the count that matters and a
 * bar to read the shape at a glance. A box to find one by name, because
 * with two hundred and seventy codes scrolling is not a search. Clicking
 * a line opens the accounts behind it, in rows, with their details.
 */
function PromoBlock({ r, fmtDate, onOpen }) {
  const [q, setQ] = useState('');

  const top = r.byPromo[0];

  const list = useMemo(() => {
    const needle = q.trim().toUpperCase();
    if (!needle) return r.byPromo;
    return r.byPromo.filter(c => c.key.includes(needle) || c.plans.join(' ').toUpperCase().includes(needle));
  }, [r.byPromo, q]);

  return (
    <Card>
      <CardBody>
        <header className="crhead">
          <div><h4>Who arrived on a promo code</h4></div>
        </header>

        {!r.byPromo.length ? (
          <EmptyState icon="tag" tone="g-slate" title="No promo codes in this window">
            Nobody who signed up in this period came in on one.
          </EmptyState>
        ) : (
          <>
            <div className="crtools">
              <div className="crfind">
                <Icon name="search" />
                <input
                  value={q}
                  placeholder={`Find a code among ${r.promo.codes.toLocaleString()}…`}
                  onChange={e => setQ(e.target.value)}
                  aria-label="Find a promo code"
                />
                {q ? (
                  <button type="button" className="crfx" onClick={() => setQ('')} aria-label="Clear the search">
                    <Icon name="x" />
                  </button>
                ) : null}
              </div>
              {/* Only while a search is narrowing the list. "402 codes, most
                  used first" beside a box that already says "among 402" was
                  the same number twice and an ordering nobody had asked
                  about. */}
              {list.length === r.byPromo.length ? null : (
                <span className="crfound">
                  {list.length.toLocaleString()} of {r.byPromo.length.toLocaleString()} codes
                </span>
              )}
            </div>

            {!list.length ? (
              <EmptyState icon="search" tone="g-slate" title="No code matches that" />
            ) : (
              /*
               * A real table, because it is one.
               *
               * It was a grid per row, and a grid resolves its tracks
               * against its own row's content — so two flexible tracks gave
               * the bar 185px on one line and 570px on the next, with every
               * column after it landing somewhere different. Two rows of the
               * same table that do not line up is not a styling problem, it
               * is the wrong element: sharing column widths down a column is
               * the one thing a table does by construction.
               *
               * `table-layout: fixed` with a colgroup makes the widths a
               * decision rather than a measurement, so a long plan name
               * cannot push the columns around either. The head sticks to
               * the top of the scroll frame, which four hundred rows want.
               */
              <div className="crcodewrap">
                <table className="crtable">
                  <colgroup>
                    <col className="c-rank" /><col className="c-code" /><col className="c-n" />
                    <col className="c-bar" /><col className="c-pay" /><col className="c-plans" />
                    <col className="c-last" /><col className="c-go" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th><span className="vh">Rank</span></th>
                      <th>Code</th>
                      <th className="r">Clients</th>
                      <th>Share of the coded</th>
                      <th className="r">Paying</th>
                      <th>Plans they took</th>
                      <th>Last arrival</th>
                      <th><span className="vh">Open</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((c, i) => {
                      const open = () => onOpen(`code:${c.key}`);
                      return (
                        /* The whole row opens the accounts behind it. A
                           button inside one cell would make the other six
                           dead space over the same answer. */
                        <tr
                          key={c.key}
                          className={c.total > 1 ? 'chan' : ''}
                          tabIndex={0}
                          role="button"
                          aria-label={`${c.key}, ${c.total} client${c.total === 1 ? '' : 's'}`}
                          onClick={open}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
                          }}
                        >
                          {/* Every row is numbered. It was only the ones used
                              more than once, which left four hundred rows with
                              a blank where the position should be. The
                              channels are still marked, by the tint. */}
                          <td className="crrank"><span>{i + 1}</span></td>
                          <td><code>{c.key}</code></td>
                          <td className="crcn"><b>{c.total.toLocaleString()}</b><small>{c.total === 1 ? 'client' : 'clients'}</small></td>
                          <td className="crcshare">
                            <span className="crcbar" aria-hidden="true">
                              <i style={{ width: `${Math.max(4, Math.round((c.total / (top.total || 1)) * 100))}%` }} />
                            </span>
                            <b>{share(c.total, r.promo.on) || '<1'}%</b>
                          </td>
                          <td className={`crcpay${c.paid ? ' ok' : ''}`}>
                            {c.paid ? `${c.paid} paying` : 'none paying'}
                          </td>
                          <td className="crcplans">
                            {c.plans.slice(0, 3).map(x => <em key={x}>{x}</em>)}
                            {c.plans.length > 3 ? <em className="more">+{c.plans.length - 3}</em> : null}
                            {!c.plans.length ? <em className="more">no plan</em> : null}
                          </td>
                          {/* Still running, or stopped in March? A count
                              cannot tell those apart, and for a code somebody
                              is sharing around that is the question. */}
                          <td className="crclast">{c.newest ? fmtDate(c.newest) : '—'}</td>
                          <td className="crcgo"><Icon name="chev" /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Who has paid, and who has not.
 *
 * Two cards and no more. It used to lead with the money and then break the
 * payers down by billing cycle — an Annual card and a Monthly card, each
 * carrying a share, an average and a monthly footing. Nine figures to
 * answer a question that has two halves, and the halves were the thing
 * being asked for.
 *
 * The money on the paid card is the amounts as they are recorded, added
 * up. Not a monthly rate: dividing an annual invoice by twelve made the
 * card disagree with the list it opens, and a figure nobody can check
 * against the rows behind it is a figure nobody trusts.
 *
 * "What the payment column says" stays hidden unless it has something to
 * say. On this book it holds YES and NO, which is the same two numbers
 * again in smaller type; it comes back the moment a PENDING or an OVERDUE
 * appears, because those are three different conversations.
 */
function PaymentBlock({ r, money, onOpen }) {
  const t = r.total.total;
  const pay = r.payment;

  /* Anything beyond the plain yes/no the two figures above already carry. */
  const oddStates = r.byPay.filter(x => !['YES', 'NO'].includes(x.key));

  /*
   * Two halves of the book, and what one of them is worth.
   *
   * The paid card carries the money, because that is the figure anybody
   * opens this page for. The unpaid card carries none, because there is
   * none: an account with nothing recorded against it is not in debt to
   * us, it simply has not paid, and a zero there would read as one.
   */
  const cards = [
    {
      k: 'paid',
      icon: 'card',
      value: money(pay.value),
      label: 'Paid',
      sub: `${pay.done.toLocaleString()} client${pay.done === 1 ? '' : 's'}`
        + ` · ${share(pay.done, t)}% of the ${t.toLocaleString()} in this window`,
      meter: share(pay.done, t) || 0,
      meterNote: pay.done
        ? `${money(Math.round(pay.value / pay.done))} each, on average`
        : 'nothing recorded yet',
      foot: [],
      open: pay.done,
      cut: 'paid'
    },
    {
      k: 'unpaid',
      icon: 'eyeoff',
      value: pay.notDone.toLocaleString(),
      label: 'Not paid',
      sub: `${share(pay.notDone, t)}% of the ${t.toLocaleString()} in this window`,
      meter: share(pay.notDone, t) || 0,
      meterNote: 'nothing recorded against them',
      foot: [],
      open: pay.notDone,
      cut: 'unpaid'
    }
  ];

  return (
    <Card>
      <CardBody>
        <header className="crhead">
          <div><h4>Who has paid</h4></div>
        </header>

        {/* Money actually recorded against the account, not a plan chosen —
            an account can sit on STARTER for a year without one. */}
        <div className="crmoney" data-n={cards.length}>
          {cards.map(f => (
            <button key={f.k} type="button" className={`crmcard ${f.k}`} onClick={() => onOpen(f.cut)}>
              <span className="crmic"><Icon name={f.icon} solid /></span>
              <span className="crmn">{f.value}</span>
              <span className="crml">{f.label}</span>
              <span className="crms">{f.sub}</span>

              <span className="crmeter" aria-hidden="true">
                <i style={{ width: `${Math.max(2, Math.min(100, f.meter))}%` }} />
              </span>
              <span className="crmnote">{f.meterNote}</span>

              <span className="crmfoot">
                {f.foot.map(([v, l]) => (
                  <span key={l}><b>{v}</b><small>{l}</small></span>
                ))}
                <em>Open the {f.open.toLocaleString()}<Icon name="chev" /></em>
              </span>
            </button>
          ))}
        </div>

        {oddStates.length ? (
          <div className="crstates">
            <h5>What else the payment column says</h5>
            <ul className="crpills">
              {oddStates.map(x => (
                <li key={x.key}>
                  <button
                    type="button"
                    className={`crpill p-${x.key.toLowerCase().replace(/[^a-z]/g, '')}`}
                    onClick={() => onOpen(`pay:${x.key}`)}
                  >
                    <span>{x.key}</span><b>{x.total.toLocaleString()}</b>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {!r.byCycle.length ? (
          <p className="crnote"><Icon name="card" />Nothing in this window has a payment against it.</p>
        ) : null}

        {/* The one thing on this block somebody has to go and fix. The
            "nothing recorded" line that used to sit beside it is gone:
            it is the Not paid card, said twice. */}
        {pay.blankAmount ? (
          <div className="crnotes">
            <button type="button" className="crgap" onClick={() => onOpen('noamount')}>
              <Icon name="alert" />
              <span>
                <b>{pay.blankAmount.toLocaleString()}</b> of the {pay.done.toLocaleString()} are marked
                paid with no amount on the record, so none of the money above counts them.
              </span>
              <Icon name="chev" />
            </button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/**
 * Who is using it: two cards, for the window the page is asking about.
 *
 * It was four rows — 7, 14, 30 and 60 days — each split into a bar and
 * two figures. Eight numbers to answer one question, and the date filter
 * above already says which window anybody means. So: active, and not
 * active, at the size a figure people quote deserves. Both open the
 * accounts behind them, because "who has gone quiet" is a list somebody
 * is going to work from rather than a number to look at.
 *
 * The full ladder is still in the exports, where a table costs nothing
 * and sizing a problem against four spans is what a spreadsheet is for.
 */
function ActivityBlock({ r, days, onOpen }) {
  const a = r.activity;
  const w = a.windows.find(x => x.primary) || a.windows[0] || { days, active: 0, idle: 0, pct: 0 };
  /* Of the whole book, because that is what the two cards count — see
     the note on `book` in summarise. */
  const t = w.active + w.idle;
  const span = days === 1 ? 'today' : `in the last ${days} days`;

  const cards = [
    {
      k: 'act',
      icon: 'zap',
      label: 'Active clients',
      n: w.active,
      pct: w.pct,
      note: `They have opened XYRA ${span}.`,
      cut: `seen:${w.days}`
    },
    {
      k: 'idle',
      icon: 'eyeoff',
      label: 'Not active clients',
      n: w.idle,
      pct: share(w.idle, t),
      note: a.neverOnBook
        ? `Nothing ${span} — including ${a.neverOnBook.toLocaleString()} the app has never seen at all.`
        : `Nothing ${span}, whatever the status column says.`,
      cut: `idle:${w.days}`
    }
  ];

  return (
    <Card>
      <CardBody>
        <header className="crhead">
          <div>
            <h4>Active and not active</h4>
            <p>
              Counted on when somebody last opened XYRA, not on the status column —
              that says Active for nearly every account on the book and so cannot
              move. <b>These two count every client</b>, not only the ones who signed
              up in the window: the date filter above sets how far back
              &ldquo;last opened&rdquo; reaches, and nothing else. Plan and owner
              still narrow it. Open either card for the accounts behind it.
            </p>
          </div>
          <div className="crhacts">
            <span className="crchip flat"><b>{windowRow(w.days)}</b></span>
            {a.neverOnBook ? (
              <button type="button" className="crchip warn" onClick={() => onOpen('never')}>
                <b>{a.neverOnBook.toLocaleString()}</b> never signed in
              </button>
            ) : null}
          </div>
        </header>

        <div className="cracards">
          {cards.map(c => (
            <button key={c.k} type="button" className={`cracard ${c.k}`} onClick={() => onOpen(c.cut)}>
              <span className="craic"><Icon name={c.icon} solid /></span>
              <span className="cran">
                {c.n.toLocaleString()}
                <em>{c.pct === null ? '' : `${c.pct}%`}</em>
              </span>
              <span className="cral">{c.label}</span>
              <span className="cras">{c.note}</span>
              {/* The two together are the whole window, so each card shows
                  its own share of it rather than a bar nobody can read. */}
              <span className="crameter" aria-hidden="true">
                <i style={{ width: `${Math.max(2, c.pct || 0)}%` }} />
              </span>
              <span className="crago">View details<Icon name="chev" /></span>
            </button>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

export default function ClientReport() {
  const { S, rows, derive, fmtMoney, fmtDate, toast } = useApp();
  const [key, setKey] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [plan, setPlan] = useState('');
  const [owner, setOwner] = useState('');
  const [open, setOpen] = useState(null);
  const [saving, setSaving] = useState('');

  /*
   * Is anything narrowing the page?
   *
   * Only then is there a Clear button. A control that is always there and
   * usually does nothing teaches people to ignore it, and the one moment it
   * matters — a filter left on from ten minutes ago, quietly explaining a
   * figure that looks wrong — is the moment it has to be noticed.
   */
  const narrowed = key !== 'all' || !!plan || !!owner || !!from || !!to;

  const clear = () => {
    setKey('all');
    setFrom('');
    setTo('');
    setPlan('');
    setOwner('');
  };

  const period = useMemo(() => rangeOf(key, { from, to }), [key, from, to]);

  /*
   * The activity window the page is asking about.
   *
   * The period's own span, so picking Today asks about today. All time has
   * no span, and falls back to the week — the figure the team already
   * quotes when nobody has said otherwise.
   */
  const seenDays = period.days || SEEN_WINDOWS[0];

  const r = useMemo(
    () => summarise(S, rows, derive, {
      from: period.from, to: period.to, plan, owner, primary: seenDays
    }),
    [S, rows, derive, period, plan, owner, seenDays]
  );

  const HEADLINE = useMemo(() => headlineFor(seenDays), [seenDays]);

  const plans = useMemo(() => plansOf(S, rows), [S, rows]);
  const owners = useMemo(() => ownersOf(rows), [rows]);

  const t = r.total;
  const figures = {
    total: t.total,
    paid: r.payment.done,
    value: t.value,
    promo: r.promo.on,
    seen: (r.activity.windows.find(w => w.primary) || {}).active || 0,
    never: r.activity.neverOnBook
  };

  /*
   * One payload, two files.
   *
   * The screen has already worked the numbers out from the rows it is
   * showing; the server lays them out rather than counting again, so the
   * workbook, the PDF and the page cannot disagree about one word.
   *
   * `period.to` goes out as the day *after* the last one included, because
   * the shared masthead prints "end not included" — on screen the pair is
   * shown inclusive, which is what somebody typing two dates means.
   */
  const payload = () => {
    const days = r.cohort
      .map(x => parseDate(x.r.regOn))
      .filter(Boolean)
      .sort((a, b) => a - b);
    const first = from || (days.length ? dayOf(days[0]) : dayOf(new Date()));
    const last = to || (days.length ? dayOf(days[days.length - 1]) : dayOf(new Date()));

    return {
      title: 'XYRA — client report',
      period: {
        label: period.label,
        sub: `${first} to ${last}`,
        from: first,
        to: dayOf(addDays(parseDate(last) || new Date(), 1)),
        days: period.days
      },
      timezone: TZ_LABEL,
      generatedAt: stamp(Date.now()),
      currency: S.currency || 'AED',
      filters: { plan: plan || 'Every plan', owner: owner || 'Everyone' },
      counted: r.excluded
        ? `Picked by the day they signed up; windows count back from today. Includes ${r.excluded} internal or test accounts.`
        : 'Accounts are picked by the day they signed up. The activity windows count back from today.',
      headline: HEADLINE.map(x => ({
        measure: x.l,
        value: x.money ? Math.round(figures[x.k]) : figures[x.k],
        money: !!x.money,
        share: x.k === 'total' || x.money ? null : share(figures[x.k], t.total),
        note: x.s
      })),
      /* The cards are the four tiers on sale; the file is a table, and
         a plan table whose rows do not add up to the total is a broken
         report. So a retired tier keeps its row here - at the end, after
         the ones being sold. */
      plans: [...r.byPlan, ...(r.offLadder || [])].map(p => ({
        key: p.key,
        price: Math.round(p.price || 0),
        total: p.total,
        paid: p.paid,
        unpaid: p.total - p.paid,
        onBook: p.live,
        seen: p.seen,
        promo: p.promo,
        value: Math.round(p.value),
        monthly: Math.round(p.mrr),
        share: share(p.total, t.total)
      })),
      promo: {
        on: r.promo.on,
        off: r.promo.off,
        codes: r.promo.codes,
        paid: r.promo.paid,
        value: Math.round(r.promo.value)
      },
      promoCodes: r.byPromo.map(c => ({
        key: c.key,
        total: c.total,
        paid: c.paid,
        share: share(c.total, r.promo.on),
        value: Math.round(c.value),
        monthly: Math.round(c.mrr),
        plans: c.plans.join(', '),
        /* Whatever the screen shows on a row has to be in the file, or the
           export is a different document from the page it came off. */
        lastArrival: c.newest ? fmtDate(c.newest) : '',
        who: c.rows.slice(0, 8).map(x => x.r.name || x.r.email || 'Untitled').join(', ')
      })),
      payment: {
        done: r.payment.done,
        notDone: r.payment.notDone,
        blankAmount: r.payment.blankAmount,
        value: Math.round(r.payment.value),
        monthly: Math.round(r.payment.mrr)
      },
      payStatus: r.byPay.map(p => ({ key: p.key, total: p.total, paid: p.paid, value: Math.round(p.value) })),
      cycles: r.byCycle.map(c => ({
        key: c.key,
        total: c.total,
        inferred: c.inferred || 0,
        value: Math.round(c.value),
        monthly: Math.round(c.mrr)
      })),
      activity: {
        live: r.activity.live,
        dormant: r.activity.dormant,
        /* The book, like the two cards - see the note on `book` in
           summarise. The cohort figure would make the file disagree with
           the chip the page shows beside them. */
        never: r.activity.neverOnBook,
        windows: r.activity.windows.map(w => ({
      days: w.days, active: w.active, idle: w.idle, pct: w.pct, primary: w.primary
        }))
      },
      clients: r.cohort.map(x => lineOf(S, x, fmtDate))
    };
  };

  const save = async kind => {
    setSaving(kind);
    try {
      const body = payload();
      const blob = kind === 'xlsx' ? await api.clients.reportBook(body) : await api.clients.reportPdf(body);
      download(blob, `xyra-client-report-${body.period.from}.${kind}`);
      toast(`Exported ${period.label.toLowerCase()} — ${t.total.toLocaleString()} clients`);
    } catch (e) {
      toast(e.message || 'That export could not be built');
    } finally {
      setSaving('');
    }
  };

  const cut = plan || owner ? ` · ${[plan, owner].filter(Boolean).join(' · ')}` : '';

  return (
    <div className="cr">
      {/* --------------------------------------------- the window */}
      <div className="crbarrow">
        <div className="crranges" role="tablist" aria-label="Period">
          {RANGES.map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={key === k}
              className={`crr${key === k ? ' on' : ''}`}
              onClick={() => setKey(k)}
            >
              {label}
            </button>
          ))}
        </div>

        {key === 'custom' ? (
          <div className="crdates">
            <label>
              <span>From</span>
              <input type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} />
            </label>
            <label>
              <span>To</span>
              <input type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} />
            </label>
          </div>
        ) : null}

        <div className="crf">
          <label htmlFor="cr-plan">Plan</label>
          <select id="cr-plan" value={plan} onChange={e => setPlan(e.target.value)}>
            <option value="">Every plan</option>
            {plans.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div className="crf">
          <label htmlFor="cr-owner">Owner</label>
          <select id="cr-owner" value={owner} onChange={e => setOwner(e.target.value)}>
            <option value="">Everyone</option>
            {owners.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        {narrowed ? (
          <button type="button" className="crclear" onClick={clear} title="Back to every account, all time">
            <Icon name="undo" />Clear filters
          </button>
        ) : null}

        <div className="cracts">
          <button type="button" className="crout xls" disabled={!!saving} onClick={() => save('xlsx')} title="A formatted workbook, five sheets">
            <span className="croi">{saving === 'xlsx' ? <Spinner /> : <Icon name="sheet" />}</span>
            <span className="crot"><b>Excel</b><small>Five sheets, filterable</small></span>
          </button>
          <button type="button" className="crout pdf" disabled={!!saving} onClick={() => save('pdf')} title="Laid out for paper">
            <span className="croi">{saving === 'pdf' ? <Spinner /> : <Icon name="file" />}</span>
            <span className="crot"><b>PDF</b><small>Laid out for paper</small></span>
          </button>
        </div>
      </div>

      <p className="crsays">
        <b>{period.label}</b>
        {period.from ? ` — ${period.from} to ${period.to}` : ' — every account on the book'}{cut}.
        {' '}{t.total.toLocaleString()} client{t.total === 1 ? '' : 's'}, counted by the day they
        signed up. All times {TZ_LABEL}.
        {/* The tile strip on All clients drops Internal and Test; this
            counts the whole window. Said only when the two differ, so a
            caveat that is always zero is not permanent furniture. */}
        {r.excluded
          ? ` Includes ${r.excluded.toLocaleString()} internal or test ${r.excluded === 1 ? 'account' : 'accounts'}, which the tiles on All clients leave out.`
          : ''}
      </p>

      {/* ------------------------------------ who is using it

          At the top, because it is the figure people come to this page
          for. The rest of the page is what those accounts are made of:
          which plan, which code, and what has been paid. */}
      <ActivityBlock r={r} days={seenDays} onOpen={setOpen} />

      {/* ---------------------------------------------- the plans */}
      <Card>
        <CardBody>
          <header className="crhead">
            <div>
              <h4>Which plan they are on</h4>
              <p>
                One card per tier, in the order the plans are sold. Every figure
                on a card opens the accounts behind it.
                {/* Retired tiers get a line, not a card — see planOrder. The
                    accounts are still on the book and still counted in every
                    other figure on this page. */}
                {(r.offLadder || []).length
                  ? ` Not every account is on one of them: ${(r.offLadder || [])
                      .map(p => `${p.total} on ${p.key === 'NO PLAN' ? 'no plan at all' : p.key}`)
                      .join(', ')}. They are counted everywhere else on this page and in the exported file.`
                  : ''}
              </p>
            </div>
          </header>

          {!r.byPlan.length ? (
            <EmptyState icon="card" tone="g-slate" title="Nobody signed up in this window" />
          ) : (
            <ol className="crcards">
              {r.byPlan.map((p, i) => (
                <PlanCard
                  key={p.key}
                  p={p}
                  of={t.total}
                  rank={i + 1}
                  money={fmtMoney}
                  days={seenDays}
                  period={period.label}
                  onOpen={setOpen}
                />
              ))}
            </ol>
          )}
        </CardBody>
      </Card>

      <PromoBlock r={r} fmtDate={fmtDate} onOpen={setOpen} />

      <PaymentBlock r={r} money={fmtMoney} onOpen={setOpen} />


      {open ? (
        <ClientDetail
          cut={open}
          report={r}
          period={period}
          days={seenDays}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}
